import { useRef, useState } from 'react';
import { AppDialog } from '../../components/AppDialog';
import { api } from '../../api/client';
import { runLoading } from '../../components/loading';
import { useNotify } from '../../components/notifications';

/** The list's file-import settings (ModuleCrudConfig.listImport). */
export interface ListImportConfig {
  /** POST target — multipart, field name `file`. Replies `{ data: { created, skipped } }`. */
  importUrl: string;
  /** GET target returning the template workbook. */
  templateUrl: string;
  /** File name the template is saved under. */
  templateName: string;
  /** Popup title. */
  dialogTitle: string;
}

type ImportReply = { data?: { created?: number; skipped?: number; noSheet?: boolean }; error?: { message?: string } };

/**
 * The legacy "Import File" popup: one Input File box with Browse, then OK / Cancel.
 * OK uploads the workbook; the server imports it and replies with the counts. A file the
 * server stops part-way through (a row with no name) still imports the rows above it, so the
 * list is refreshed on a refusal as well as on success.
 */
export function ImportFileDialog({ config, onClose, onImported }: {
  config: ListImportConfig;
  onClose: () => void;
  /** Called whenever rows may have been added (success, or a part-way stop). */
  onImported: () => void | Promise<void>;
}) {
  const notify = useNotify();
  const pickRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { notify.warning('Please upload a file to proceed.'); return; }
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      const res = await runLoading(api.post<ImportReply>(config.importUrl, form),
        { blocking: true, label: `Importing ${file.name}…` });
      const d = res.data?.data;
      // Nothing to read in the file — not an error (legacy raises none); just say nothing came in.
      if (d?.noSheet) { notify.info('Nothing was imported: the file has no Parameter sheet.'); onClose(); return; }
      await onImported();
      const skipped = d?.skipped ?? 0;
      notify.success(`Imported successfully. ${d?.created ?? 0} added${skipped > 0 ? `, ${skipped} already existed` : ''}.`);
      onClose();
    } catch (err: unknown) {
      const reply = (err as { response?: { data?: ImportReply } }).response?.data;
      const added = reply?.data?.created ?? 0;
      if (added > 0) await onImported();
      notify.error(`${reply?.error?.message ?? 'Import failed.'}${added > 0 ? ` ${added} row(s) above it were imported.` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open
      title={config.dialogTitle}
      width={520}
      className="dlg-center"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={busy} onClick={() => void submit()}>OK</button>
          <button type="button" className="ca-btn" disabled={busy} onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="ifd-row">
        <label className="ifd-label" htmlFor="ifd-file">Input File:</label>
        <input id="ifd-file" className="ifd-name" type="text" readOnly value={file?.name ?? ''}
          onClick={() => pickRef.current?.click()} />
        <button type="button" className="ca-btn" onClick={() => pickRef.current?.click()}>Browse…</button>
        <input ref={pickRef} type="file" accept=".xlsx" style={{ display: 'none' }}
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />
      </div>
    </AppDialog>
  );
}
